import { useEffect, useRef, useState } from "react";
import type { MagicModelReq } from "../ar/magicModel";
import { modelTurnNow, mountModel } from "../ar/magicModelMount";
import { guardLibGlobals } from "../ar/libGlobals";

/**
 * 🧊 **책 뷰어 안의 3D 무대** — 카메라 없이 「✨ 마법 보기」에서 실물 3D 를 세운다(C안, 2026-09-13).
 *
 * ## 왜
 * 카메라가 안 되는 곳(카톡 안 브라우저·교실 태블릿·카메라 거부)에서도 28쪽 수막새를 **돌려 보고 키워 보고 기울여**
 * 볼 수 있어야 한다. 그 전까지 책 뷰어는 24장 띠만 돌렸다.
 *
 * ## 🔴 엔진은 카메라 AR 과 같은 A-Frame — 여기서만 **늦게** 싣는다
 * 책을 여는 것만으로 엔진(수백 KB)이 내려오면 안 된다. 3D 가 있는 새 화면이 열릴 때만 `import("aframe")`.
 * 못 실으면(WebGL 없음·차단) `onFailed` → 부모가 띠로 돌아간다. 세우는 손은 카메라와 **같은 것**(`magicModelMount`).
 *
 * ## 무대 좌표
 * 카메라 AR 과 같게 — 책 면이 z=0 평면, 카메라가 +z 에서 본다. 3D 는 책 면 위에 살짝 떠 있고(`MODEL_LIFT`)
 * 그림자는 책 면에 진다. 그래서 카메라에서 본 것과 책에서 본 것이 같은 모양이다.
 */
interface Props {
  req: MagicModelReq;
  onFailed: () => void;
}

/** 카메라 거리·시야 — 책 폭 0.62 짜리 물건이 무대의 절반쯤 차게. */
const CAMERA_Z = 1.4;
const CAMERA_FOV = 50;

export function BookModelStage({ req, onFailed }: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLElement | null>(null);
  const [ready, setReady] = useState(false);
  const reqRef = useRef(req);
  reqRef.current = req;
  const failRef = useRef(onFailed);
  failRef.current = onFailed;

  useEffect(() => {
    let disposed = false;
    const host = hostRef.current;
    if (!host) return;
    // 🧹 카메라 AR 과 같은 엔진이라 같은 리스너가 남는다(ar/libGlobals.ts)
    const libGuard = guardLibGlobals();
    type SceneEl = HTMLElement & { renderer?: { dispose?: () => void } };
    let scene: SceneEl | null = null;
    (async () => {
      try {
        await import("aframe"); // 전역 AFRAME 등록 — 카메라 AR 과 같은 엔진
      } catch {
        // 못 실었다 — 부모가 띠로 돌아간다(조용히 빈 무대를 두면 아이는 「아무것도 없음」을 본다)
        if (!disposed) failRef.current();
        return;
      }
      if (disposed) return;
      const el = document.createElement("a-scene") as SceneEl;
      el.setAttribute("embedded", "");
      el.setAttribute("vr-mode-ui", "enabled: false");
      el.setAttribute("device-orientation-permission-ui", "enabled: false");
      el.setAttribute("renderer", "colorManagement: true; alpha: true");
      el.setAttribute("shadow", "type: pcfsoft");
      el.setAttribute("background", "transparent: true");
      el.setAttribute("data-book-model-stage", "");
      const cam = document.createElement("a-entity");
      cam.setAttribute("camera", `fov: ${CAMERA_FOV}; near: 0.05; far: 20`);
      cam.setAttribute("position", `0 0 ${CAMERA_Z}`);
      cam.setAttribute("look-controls", "enabled: false");
      cam.setAttribute("wasd-controls", "enabled: false");
      el.appendChild(cam);
      const root = document.createElement("a-entity"); // 책 면 좌표계 — 카메라 AR 의 wrapper 와 같은 자리
      el.appendChild(root);
      host.appendChild(el);
      scene = el;
      rootRef.current = root;
      setReady(true);
    })();
    return () => {
      disposed = true;
      rootRef.current = null;
      scene?.renderer?.dispose?.();
      scene?.remove();
      libGuard.release();
    };
  }, []);

  // 각도·배율이 바뀔 때마다 — 세우는 손은 카메라 AR 과 같은 것
  useEffect(() => {
    const root = rootRef.current;
    if (!ready || !root) return;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false;
    mountModel(root, req, { reducedMotion: reduced, onFailed: () => failRef.current() });
  }, [ready, req]);

  return <div ref={hostRef} className="magic__model" data-magic-model-stage aria-hidden="true" />;
}

/** 지금 3D 가 서 있는 각도(0~1) — 부모가 「잡을 때 이어받기」에 쓴다. 무대가 없으면 `null`. */
export function bookModelTurnNow(stage: Element | null | undefined): number | null {
  return modelTurnNow(stage?.querySelector("[data-book-model-stage]") ?? null);
}
